/**
 * Hooks react-query de la zone Paramètres (intégrations ticketing, webhooks, recettes, base de connaissances,
 * domaine personnalisé, zone danger). Même convention que hooks.ts : clés ['org', orgId, ...].
 */
import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useApi } from './ApiProvider';
import { useOrgId } from '../auth/store';
import { keys } from './hooks';
import type { ApiClient } from './client';
import type { KbDocument, TicketingKind } from './types';

export const settingsKeys = {
  integrationConfig: (org: string, kind: TicketingKind) => ['org', org, 'integrations', 'config', kind] as const,
  webhooks: (org: string) => ['org', org, 'webhooks'] as const,
  deliveries: (org: string, id: string) => ['org', org, 'webhooks', id, 'deliveries'] as const,
  recipes: (org: string) => ['org', org, 'recipes'] as const,
  kb: (org: string) => ['org', org, 'knowledge'] as const,
  domain: (org: string) => ['org', org, 'domain-verification'] as const,
};

/** Mutation scopée à l'organisation active ; invalide les clés indiquées puis le journal d'audit. */
function useMut<TVars, TResult>(
  fn: (api: ApiClient, org: string, vars: TVars) => Promise<TResult>,
  invalidate: (org: string) => QueryKey[],
) {
  const api = useApi();
  const org = useOrgId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: TVars) => fn(api, org, vars),
    onSuccess: async () => {
      await Promise.all([...invalidate(org), keys.audit(org)].map((queryKey) => qc.invalidateQueries({ queryKey })));
    },
  });
}

/* ---------- Intégrations ticketing ---------- */

export function useIntegrationConfig(kind: TicketingKind, enabled = true) {
  const api = useApi();
  const org = useOrgId();
  return useQuery({ queryKey: settingsKeys.integrationConfig(org, kind), queryFn: () => api.getIntegrationConfig(org, kind), enabled });
}

const cfgKeys = (org: string, kind: TicketingKind) => [settingsKeys.integrationConfig(org, kind), keys.integrations(org)];

export const useSaveIntegrationConfig = (kind: TicketingKind) =>
  useMut((api, org, input: Parameters<ApiClient['saveIntegrationConfig']>[2]) => api.saveIntegrationConfig(org, kind, input), (org) => cfgKeys(org, kind));

export const useTestIntegration = (kind: TicketingKind) =>
  useMut((api, org, _: void) => api.testIntegration(org, kind), (org) => cfgKeys(org, kind));

/* ---------- Webhooks ---------- */

export function useWebhooks() {
  const api = useApi();
  const org = useOrgId();
  return useQuery({ queryKey: settingsKeys.webhooks(org), queryFn: () => api.listWebhooks(org) });
}

export function useWebhookDeliveries(id: string | null) {
  const api = useApi();
  const org = useOrgId();
  return useQuery({ queryKey: settingsKeys.deliveries(org, id ?? ''), queryFn: () => api.listWebhookDeliveries(org, id!), enabled: Boolean(id) });
}

export const useCreateWebhook = () =>
  useMut((api, org, input: Parameters<ApiClient['createWebhook']>[1]) => api.createWebhook(org, input), (org) => [settingsKeys.webhooks(org)]);
export const useUpdateWebhook = () =>
  useMut((api, org, v: { id: string; patch: Parameters<ApiClient['updateWebhook']>[2] }) => api.updateWebhook(org, v.id, v.patch), (org) => [settingsKeys.webhooks(org)]);
export const useRotateWebhookSecret = () =>
  useMut((api, org, id: string) => api.rotateWebhookSecret(org, id), (org) => [settingsKeys.webhooks(org)]);
export const useDeleteWebhook = () => useMut((api, org, id: string) => api.deleteWebhook(org, id), (org) => [settingsKeys.webhooks(org)]);
export const useReplayWebhookDelivery = () =>
  useMut((api, org, v: { id: string; deliveryId: string }) => api.replayWebhookDelivery(org, v.id, v.deliveryId), (org) => [settingsKeys.webhooks(org)]);

/* ---------- Recettes d'organisation ---------- */

export function useRecipes() {
  const api = useApi();
  const org = useOrgId();
  return useQuery({ queryKey: settingsKeys.recipes(org), queryFn: () => api.listRecipes(org) });
}

const rcp = (org: string) => [settingsKeys.recipes(org)];
export const useCreateRecipe = () => useMut((api, org, i: Parameters<ApiClient['createRecipe']>[1]) => api.createRecipe(org, i), rcp);
export const useUpdateRecipe = () =>
  useMut((api, org, v: { id: string; input: Parameters<ApiClient['updateRecipe']>[2] }) => api.updateRecipe(org, v.id, v.input), rcp);
export const useDeleteRecipe = () => useMut((api, org, id: string) => api.deleteRecipe(org, id), rcp);
export const useImportRecipe = () => useMut((api, org, libraryId: string) => api.importRecipe(org, libraryId), rcp);
export const useSubmitRecipe = () => useMut((api, org, id: string) => api.submitRecipe(org, id), rcp);
export const useReviewRecipe = () =>
  useMut((api, org, v: { id: string; decision: 'approve' | 'reject' }) => api.reviewRecipe(org, v.id, v.decision), rcp);
export const useStartRollout = () =>
  useMut((api, org, v: { id: string; failureThresholdPct: number }) => api.startRollout(org, v.id, v.failureThresholdPct), rcp);
export const useAdvanceRollout = () => useMut((api, org, id: string) => api.advanceRollout(org, id), rcp);
export const useHaltRollout = () => useMut((api, org, id: string) => api.haltRollout(org, id), rcp);

/* ---------- Base de connaissances ---------- */

/** Liste des documents ; se rafraîchit tant qu'une indexation est en cours. */
export function useKbDocuments() {
  const api = useApi();
  const org = useOrgId();
  return useQuery({
    queryKey: settingsKeys.kb(org),
    queryFn: () => api.listKbDocuments(org),
    refetchInterval: (q) => ((q.state.data as KbDocument[] | undefined)?.some((d) => d.status === 'queued' || d.status === 'indexing') ? 1500 : false),
  });
}

const kb = (org: string) => [settingsKeys.kb(org)];
export const useAddKbDocument = () => useMut((api, org, i: Parameters<ApiClient['addKbDocument']>[1]) => api.addKbDocument(org, i), kb);
export const useRetryKbDocument = () => useMut((api, org, id: string) => api.retryKbDocument(org, id), kb);
export const useDeleteKbDocument = () => useMut((api, org, id: string) => api.deleteKbDocument(org, id), kb);

/* ---------- Domaine personnalisé ---------- */

export function useDomainVerification() {
  const api = useApi();
  const org = useOrgId();
  return useQuery({ queryKey: settingsKeys.domain(org), queryFn: () => api.getDomainVerification(org) });
}

export const useVerifyCustomDomain = () =>
  useMut((api, org, _: void) => api.verifyCustomDomain(org), (org) => [settingsKeys.domain(org), keys.settings(org)]);

/* ---------- Zone danger ---------- */

export const useExportOrganizationData = () => useMut((api, org, _: void) => api.exportOrganizationData(org), () => []);

/** Suppression d'organisation : l'appelant doit ensuite vider le cache et quitter l'organisation. */
export function useDeleteOrganization() {
  const api = useApi();
  const org = useOrgId();
  return useMutation({ mutationFn: (confirmName: string) => api.deleteOrganization(org, { confirmName }) });
}
